import { Component, ViewEncapsulation, Input, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ColorPickerDirective } from 'ngx-color-picker';
import { MatMenuTrigger, MatMenuModule } from '@angular/material/menu';
import { Addresses } from 'src/providers/customer/addresses';
import { TagService } from './tag.service';
import { Subscription } from 'rxjs';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

@Component({
    selector: 'tidy-tag',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./tag.component.scss'],
    imports: [ColorPickerDirective, MatMenuModule],
    template: `
    <div class="tag-container" [matMenuTriggerFor]="colorMenu" (click)="$event.stopPropagation()">
      <span class="material-symbols-outlined" [style.color]="(color === '#000000' || color === '#1E2125') ? defaultColor : (color || defaultColor)" [style.outline]="isSelected ? '2px solid #41cab7' : 'none'">home</span>
    </div>

    <span
      #colorPickerTrigger
      [(colorPicker)]="customColor"
      [cpOKButton]="true"
      [cpCancelButton]="true"
      [cpWidth]="230"
      (colorPickerSelect)="onCustomColorSelect($event)"
      style="position: fixed; top: 50%; left: 50%; width: 1px; height: 1px; opacity: 0; pointer-events: none;">
    </span>

    <mat-menu #colorMenu="matMenu" class="color-picker-menu">
      <div (click)="$event.stopPropagation()">
        <div class="color-grid">
          @for (colorRow of colorPalette; track colorRow) {
            <div class="color-row">
              @for (colorHex of colorRow; track colorHex) {
                <div
                  class="color-option"
                  [style.background-color]="colorHex"
                  [style.outline]="color === colorHex ? '2px solid #41cab7' : 'none'"
                  (click)="onColorSelect(colorHex)">
                </div>
              }
            </div>
          }
        </div>
        <div class="custom-color-option">
          <span class="custom-color-text" (click)="openCustomColorPicker($event)">
            Custom Color
          </span>
        </div>
      </div>
    </mat-menu>
    `,
    standalone: true
})
export class TagComponent implements OnInit, OnDestroy {

  @Input() color = '#000000';
  @Input() addressId: string;
  @Input() isSelected = false;
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  @ViewChild(ColorPickerDirective) colorPickerDirective: ColorPickerDirective;

  defaultColor = document.body.classList.contains('dark-mode') ? '#FFFFFF' : '#000000';
  private subscription: Subscription;
  customColor: string;

  constructor(
    private addressesProvider: Addresses,
    private tagService: TagService,
    private cdr: ChangeDetectorRef,
    private storage: TidyStorage
  ) {}

  colorPalette = [
    ['#C6F1DC', '#F5E7A8', '#F9DFCB', '#F8D7D3', '#DED8FA'],
    ['#72CB9B', '#EFCE60', '#F1A76E', '#E7796D', '#9C90E9'],
    ['#41825D', '#8E7023', '#B45821', '#B94335', '#6B5EC0'],
    ['#D0DFFC', '#CEECF9', '#D9F0AF', '#F6D2EA', '#DDDFE3'],
    ['#679BF8', '#81C1DD', '#9FC65B', '#D87AB8', '#8790A0'],
    ['#2C64DC', '#3F7B98', '#637E33', '#A24D84', '#646F84']
  ];

  ngOnInit() {
    this.subscription = this.tagService.colorUpdates$.subscribe(update => {
      if (update && update.addressId === this.addressId) {
        this.color = update.color;
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async selectColor(colorHex: string) {
    this.color = colorHex;
    const payload = {
      color: colorHex
    }
    this.tagService.updateColor(this.addressId, colorHex);
    await this.addressesProvider.updateAddress(payload, this.addressId);
    const addresses = await this.storage.retrieve('addresses');
    const updatedAddresses = addresses.map(address => {
      if (address.id === this.addressId) {
        return { ...address, color: colorHex };
      }
      return address;
    });
    await this.storage.save('addresses', updatedAddresses);
  }

  onColorSelect(colorHex: string) {
    this.menuTrigger.closeMenu();
    this.selectColor(colorHex);
  }

  onCustomColorSelect(color: string) {
    this.selectColor(color);
  }

  openCustomColorPicker(event: Event) {
    event.stopPropagation();
    this.customColor = this.color;
    this.menuTrigger.closeMenu();
    setTimeout(() => {
      this.colorPickerDirective.openDialog();
    }, 100);
  }

}
